// Copyright (C) 2026 — self-hosted Stremio additions
//
// Shared profile state for the self-hosted backend. Both the nav-bar avatar and
// the menu list read from here, so a switch updates both without a reload and
// without two components fetching independently and drifting apart.

const React = require('react');

const API = 'https://my.stremio.chanclatoen.nl/api';

// A deterministic colour per profile name, so a profile always looks the same
// without storing or uploading an image.
const PROFILE_COLORS = ['#7b5cff', '#2e9bff', '#00b894', '#e17055', '#d63384', '#f0a500', '#0aa2c0', '#c44dff'];

const profileColor = (name) => {
    let h = 0;
    for (let i = 0; i < String(name).length; i++) h = (h * 31 + String(name).charCodeAt(i)) >>> 0;
    return PROFILE_COLORS[h % PROFILE_COLORS.length];
};

const profileInitial = (name) => String(name || '?').trim().slice(0, 1).toUpperCase();


// Preset avatars: emoji on a coloured disc. Zero assets, zero hosting, look
// good at any size, and instantly available — the alternative was bundling
// image files or depending on an external avatar service.
const PRESETS = [
    { id: 'fox',     emoji: '\uD83E\uDD8A', bg: '#e17055' },
    { id: 'cat',     emoji: '\uD83D\uDC31', bg: '#f0a500' },
    { id: 'panda',   emoji: '\uD83D\uDC3C', bg: '#2e9bff' },
    { id: 'lion',    emoji: '\uD83E\uDD81', bg: '#d4a017' },
    { id: 'frog',    emoji: '\uD83D\uDC38', bg: '#00b894' },
    { id: 'owl',     emoji: '\uD83E\uDD89', bg: '#7b5cff' },
    { id: 'octopus', emoji: '\uD83D\uDC19', bg: '#c44dff' },
    { id: 'penguin', emoji: '\uD83D\uDC27', bg: '#0aa2c0' },
    { id: 'bear',    emoji: '\uD83D\uDC3B', bg: '#a0522d' },
    { id: 'unicorn', emoji: '\uD83E\uDD84', bg: '#ff6ba9' },
    { id: 'robot',   emoji: '\uD83E\uDD16', bg: '#5c6f7b' },
    { id: 'alien',   emoji: '\uD83D\uDC7D', bg: '#3fb950' },
    { id: 'rocket',  emoji: '\uD83D\uDE80', bg: '#d63384' },
    { id: 'game',    emoji: '\uD83C\uDFAE', bg: '#6c5ce7' },
    { id: 'popcorn', emoji: '\uD83C\uDF7F', bg: '#e8b923' },
    { id: 'ghost',   emoji: '\uD83D\uDC7B', bg: '#8395a7' }
];

const presetById = (id) => PRESETS.find((p) => p.id === id) || null;

/**
 * Resolve what to draw for a profile.
 *  { kind: 'image',  src }            — uploaded data URI
 *  { kind: 'emoji',  emoji, bg }      — chosen preset
 *  { kind: 'initial', text, bg }      — fallback
 */
const resolveAvatar = (p) => {
    const a = p && p.avatar ? String(p.avatar) : '';
    if (a.startsWith('data:image/')) return { kind: 'image', src: a };
    if (a.startsWith('preset:')) {
        const preset = presetById(a.slice(7));
        if (preset) return { kind: 'emoji', emoji: preset.emoji, bg: preset.bg };
    }
    return { kind: 'initial', text: profileInitial(p && p.name), bg: profileColor((p && p.name) || '?') };
};

let state = { profiles: [], active: null, loaded: false };
const subscribers = new Set();
const emit = () => { subscribers.forEach((fn) => fn()); };

const refresh = (authKey) => {
    if (!authKey) {
        state = { profiles: [], active: null, loaded: true };
        emit();
        return Promise.resolve(state);
    }
    return fetch(`${API}/profilesList`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ authKey })
    })
        .then((res) => res.json())
        .then((data) => {
            if (!data.result) throw new Error('no result');
            state = { profiles: data.result.profiles, active: data.result.active, loaded: true };
            emit();
            return state;
        })
        .catch(() => {
            // backend unreachable — callers fall back to stock behaviour
            state = { profiles: [], active: null, loaded: true };
            emit();
            return state;
        });
};

const setActive = (profileId) => {
    state = { ...state, active: profileId };
    emit();
};

const useSelfHostProfiles = (authKey) => {
    const [, forceRender] = React.useReducer((x) => x + 1, 0);
    React.useEffect(() => {
        subscribers.add(forceRender);
        return () => { subscribers.delete(forceRender); };
    }, []);
    React.useEffect(() => { refresh(authKey); }, [authKey]);
    return state;
};

module.exports = { API, profileColor, profileInitial, useSelfHostProfiles, refresh, setActive, PRESETS, presetById, resolveAvatar };
